import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { ReportPeriodContext } from '../../context/report-periods/report-periods'
import { UserDetailsContext } from '../../context/user-details/user-details'
import ReportActionService from '../../shared/services/report-action/report-action.service'
import LocationService from '../../shared/services/location/location.service'
import { IReportAction } from '../../shared/services/report-action/report-action.model'
import { actionEntityName, friendlyActionTitle } from '../../shared/services/report-action/report-action-title'
import { IHeadCell } from '../../shared/interfaces/table-display.model'
import { TableDisplay } from '../presentation-components/miscellaneous-components/table-display'
import { ActionStatusChip } from '../presentation-components/emissons-components/overview/emissions-actions/action-status-chip'
import { InformationBox } from '../presentation-components/miscellaneous-components/information-box'
import { PageOuterContainer } from '../../root-components/layouts/page-outer-container'
import { PageHeading } from '../presentation-components/display-elements-components/page-heading'
import { IconName } from '../../shared/interfaces/icon-picker.model'

// The data owner's task list, built on the existing emission-actions (report-action) system.
// It shows the report actions assigned to this user for the open reporting period, with the same
// status filter and row-to-action navigation the rest of the app uses, but focused (no admin
// team-actions panel).
export const DataOwnerTasksPage = () => {
  const navigate = useNavigate()
  const reportActionService = new ReportActionService()
  const reportPeriodContext = useContext(ReportPeriodContext)
  const userDetailsContext = useContext(UserDetailsContext)
  const userDetails = userDetailsContext.userDetails

  const [status, setStatus] = useState<'outstanding' | 'upcoming' | 'completed'>('outstanding')
  const [actions, setActions] = useState<IReportAction[]>([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [outstandingCount, setOutstandingCount] = useState<number>(0)
  const [inputPage, setInputPage] = useState(0)
  const [perPage, setPerPage] = useState(8)
  const [selected, setSelected] = useState<string[]>([])
  const [entityName, setEntityName] = useState<string>('')

  const reportPeriodId = reportPeriodContext.lastSelectedOpenReportId
  const reportPeriodName = useMemo(
    () => reportPeriodContext.allReportPeriods.find(p => p.id === reportPeriodId)?.friendlyName,
    [reportPeriodContext.allReportPeriods, reportPeriodId],
  )

  // The single entity this data owner is scoped to (for the friendly subtitle).
  useEffect(() => {
    const scope = userDetails?.entityScope
    const ids = scope && scope.accessAllLocations === false ? scope.locationIds : []
    if (!ids || ids.length === 0) { setEntityName(''); return }
    new LocationService().getAllLocations()
      .then((locations: any[]) => {
        const names = locations.filter(l => ids.includes(l.id)).map(l => l.friendlyName)
        setEntityName(names.length > 1 ? `your ${names.length} sites` : names.join(', '))
      })
      .catch(() => setEntityName(''))
  }, [userDetails?.id])

  const loadActions = () => {
    if (!reportPeriodId || !userDetails?.id) return
    reportActionService.getReportActionsWithPagination(reportPeriodId, `&userId=${userDetails.id}&status=${status}`, inputPage, perPage)
      .then(response => { setActions(response.rows); setTotalRecords(response.count) })
      .catch(err => console.log(err))
  }
  useEffect(loadActions, [reportPeriodId, userDetails?.id, status, inputPage, perPage])

  // Headline outstanding count (independent of the current filter/paging).
  useEffect(() => {
    if (!reportPeriodId || !userDetails?.id) return
    reportActionService.getReportActionsWithPagination(reportPeriodId, `&userId=${userDetails.id}&status=outstanding`, 0, 1)
      .then(response => setOutstandingCount(response.count))
      .catch(() => setOutstandingCount(0))
  }, [reportPeriodId, userDetails?.id, status])

  const statusHandler = (_: React.MouseEvent<HTMLElement>, next: typeof status | null) => {
    if (next) { setStatus(next); setInputPage(0) }
  }

  // Row click opens the same action-entry flow used everywhere else. Tasks created from
  // readiness findings have no entity entry form; they carry the finding's deep link instead.
  const handleSelected = (rowIds: string[]) => {
    if (!rowIds[0]) return
    const action = actions.find(a => a.id === rowIds[0])
    if (action?.actionCode === ('readiness' as IReportAction['actionCode'])) {
      navigate(action.deepLink || '/my-tasks')
      return
    }
    reportActionService.checkIfActionReportOrReduction(rowIds[0])
      .then(response => {
        if (response.type === 'reportAction') {
          navigate(`/emissions/overview/group/${response.action.groupId}?actionId=${response.action.id}`, { state: { response } })
        }
      })
      .catch(err => console.log(err))
  }

  const headCells: IHeadCell[] = [
    { id: 'title', numeric: false, label: 'Task', sortable: false },
    { id: 'entity', numeric: false, label: 'Entity', sortable: false },
    { id: 'date', numeric: false, label: 'Delivery date', sortable: false },
  ]
  const rows = actions.map(action => ({
    id: action.id,
    title: action.status
      ? <Stack direction='row' alignItems='center' gap={1}>
        <Typography variant='body2'>{friendlyActionTitle(action)}</Typography>
        <ActionStatusChip status={action.status} reason={action.statusReason} />
      </Stack>
      : friendlyActionTitle(action),
    entity: actionEntityName(action),
    date: action.actionBillingPeriod === 'annually'
      ? (moment(action.startDate).year() === moment(action.endDate).year()
        ? moment(action.startDate).year()
        : `${moment(action.startDate).year()} - ${moment(action.endDate).year()}`)
      : moment(action.endDate).format('D MMM YYYY'),
  }))

  return (
    <PageOuterContainer>
      <PageHeading title='My Tasks' />
      <Typography variant='body1' sx={{ color: 'text.secondary', mb: 3 }}>
        {outstandingCount > 0
          ? `You have ${outstandingCount} outstanding data ${outstandingCount === 1 ? 'task' : 'tasks'}${entityName ? ` for ${entityName}` : ''}${reportPeriodName ? `, ${reportPeriodName}` : ''}.`
          : `You're all caught up${entityName ? ` for ${entityName}` : ''}${reportPeriodName ? `, ${reportPeriodName}` : ''}.`}
      </Typography>

      <Box sx={{ borderWidth: '1px', borderStyle: 'solid', borderColor: 'lightGrey.main', borderRadius: '10px', p: 4 }}>
        <ToggleButtonGroup value={status} exclusive onChange={statusHandler} sx={{ mb: 3 }}>
          <ToggleButton value='outstanding' sx={{ py: 0.75, width: '180px', textTransform: 'none' }}>Outstanding</ToggleButton>
          <ToggleButton value='upcoming' sx={{ py: 0.75, width: '180px', textTransform: 'none' }}>Upcoming</ToggleButton>
          <ToggleButton value='completed' sx={{ py: 0.75, width: '180px', textTransform: 'none' }}>Completed</ToggleButton>
        </ToggleButtonGroup>

        {actions.length > 0
          ? <TableDisplay
            headCells={headCells}
            rows={rows}
            selected={selected}
            setSelected={setSelected}
            handleSelected={handleSelected}
            inputPage={inputPage}
            setInputPage={setInputPage}
            totalRecords={totalRecords}
            perPage={perPage}
            setPerPage={setPerPage} />
          : <InformationBox icon={IconName.CheckCircle} text={`You have no ${status} tasks for this reporting period.`} marginTop={1} />}
      </Box>
    </PageOuterContainer>
  )
}
